import React, { useEffect, useState, useCallback, useRef } from "react";
import { AntdButton, Inner, Section } from "./styles.js";
import { Form, Select, InputNumber } from "antd";
import { Typo } from "../../../../../../components/layout/theme/index.js";
import { useReport } from "../../../../../../contexts/ReportContext.js";
import {
	get_car_brand_models,
	get_car_brand_models_search,
	get_car_brands_by_year,
	get_car_brands_search,
	get_car_model_info,
} from "../../../../../../api/endpoints/cars.js";
import { useAxiosWithAuthInfoAuto } from "../../../../../../utils/fetchWithAuth.js";

// Función debounce personalizada
const useDebounce = (callback, delay) => {
	const timeoutRef = useRef(null);
	
	return useCallback((...args) => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		
		timeoutRef.current = setTimeout(() => {
			callback(...args);
		}, delay);
	}, [callback, delay]);
};

const CarSearch = ({ nextSubStep, isMobile }) => {
	const { reportForm, updateReportForm } = useReport();
	const fetchWithAuth = useAxiosWithAuthInfoAuto();
	const [form] = Form.useForm();
	
	// Estados principales
	const [year, setYear] = useState(null);
	const [brands, setBrands] = useState([]);
	const [models, setModels] = useState([]);
	const [selectedBrand, setSelectedBrand] = useState(null);
	const [selectedModel, setSelectedModel] = useState(null);
	const [initialValues, setInitialValues] = useState(null);
	
	// Estados de loading
	const [loadingBrands, setLoadingBrands] = useState(false);
	const [loadingModels, setLoadingModels] = useState(false);

	// Inicializar valores del formulario
	useEffect(() => {
		const fetch_initial_values = () => {
			const values = {
				year: reportForm?.carData?.year,
				brandId: reportForm?.carData?.brandId,
				modelId: reportForm?.carData?.modelId,
			};

			// Marcar que estamos en modo restauración solo si hay datos completos previos (año + marca)
			if (values.year && values.brandId) {
				isRestoringRef.current = true;
			}

			setYear(values.year);
			setSelectedBrand(values.brandId);
			setSelectedModel(values.modelId);
			setInitialValues(values);
		};
		fetch_initial_values();
	}, [reportForm]);

	// Cargar marcas por año (carga inicial)
	const loadBrandsByYear = useCallback(async (carYear) => {
		if (!carYear) return;
		
		setLoadingBrands(true);
		try {
			const res = await get_car_brands_by_year(fetchWithAuth, carYear);
			const brandOptions = res?.map((b) => ({
				value: b.id,
				label: b.name,
			})) || [];
			setBrands(brandOptions);
		} catch (error) {
			console.error("Error loading brands:", error);
			setBrands([]);
		} finally {
			setLoadingBrands(false);
		}
	}, [fetchWithAuth]);

	// Función de búsqueda de marcas
	const performBrandSearch = useCallback(async (carYear, searchTerm) => {
		if (!carYear) return;
		
		setLoadingBrands(true);
		try {
			const res = searchTerm 
				? await get_car_brands_search(fetchWithAuth, carYear, searchTerm)
				: await get_car_brands_by_year(fetchWithAuth, carYear);
			
			const brandOptions = res?.map((b) => ({
				value: b.id,
				label: b.name,
			})) || [];
			setBrands(brandOptions);
		} catch (error) {
			console.error("Error searching brands:", error);
			setBrands([]);
		} finally {
			setLoadingBrands(false);
		}
	}, [fetchWithAuth]);

	// Búsqueda dinámica de marcas con debounce
	const searchBrands = useDebounce(performBrandSearch, 300);

	// Cargar modelos por marca (ahora el filtro por año se hace en la API via cars.js)
	const loadModelsByBrand = useCallback(async (brandId, carYear) => {
		if (!brandId) return;
		
		setLoadingModels(true);
		try {
			const res = await get_car_brand_models(fetchWithAuth, brandId, carYear);
			
			const modelOptions = (res || []).map((m) => ({
				value: m.codia,
				label: m.description,
				position: m.position,
				photo_url: m.photo_url,
			}));
			setModels(modelOptions);
		} catch (error) {
			console.error("Error loading models:", error);
			setModels([]);
		} finally {
			setLoadingModels(false);
		}
	}, [fetchWithAuth]);

	// Función de búsqueda de modelos (ahora el filtro por año se hace en la API via cars.js)
	const performModelSearch = useCallback(async (brandId, searchTerm) => {
		if (!brandId) return;
		
		setLoadingModels(true);
		try {
			const res = searchTerm && searchTerm.length >= 1
				? await get_car_brand_models_search(fetchWithAuth, brandId, searchTerm, year)
				: await get_car_brand_models(fetchWithAuth, brandId, year);
			
			const modelOptions = (res || []).map((m) => ({
				value: m.codia,
				label: m.description,
				position: m.position,
				photo_url: m.photo_url,
			}));
			setModels(modelOptions);
		} catch (error) {
			console.error("Error searching models:", error);
			setModels([]);
		} finally {
			setLoadingModels(false);
		}
	}, [fetchWithAuth, year]);

	// Búsqueda dinámica de modelos con debounce
	const searchModels = useDebounce(performModelSearch, 300);

	// Flag para distinguir restauración inicial de interacción del usuario
	const isRestoringRef = useRef(false);

	// Cargar marcas y modelos cuando se restauran valores iniciales (ej: al volver atrás)
	const hasRestoredRef = useRef(false);
	useEffect(() => {
		const restoreData = async () => {
			if (hasRestoredRef.current) return;
			if (!initialValues?.year) return;
			if (!initialValues?.brandId) return;
			
			hasRestoredRef.current = true;
			
			// Cargar marcas
			setLoadingBrands(true);
			try {
				const brandsRes = await get_car_brands_by_year(fetchWithAuth, initialValues.year);
				const brandOptions = brandsRes?.map((b) => ({
					value: b.id,
					label: b.name,
				})) || [];
				setBrands(brandOptions);
			} catch (error) {
				console.error("Error restoring brands:", error);
				setBrands([]);
			} finally {
				setLoadingBrands(false);
			}

			// Cargar modelo seleccionado directamente por CODIA (más confiable que buscar en la lista paginada)
			if (initialValues.modelId) {
				setLoadingModels(true);
				try {
					const modelInfo = await get_car_model_info(fetchWithAuth, initialValues.modelId);
					if (modelInfo) {
						setModels([{
							value: modelInfo.codia,
							label: modelInfo.description,
							position: modelInfo.position,
							photo_url: modelInfo.photo_url,
						}]);
					}
				} catch (error) {
					// Fallback: usar el nombre guardado en reportForm
					const modelName = reportForm?.carData?.modelName || reportForm?.carData?.description;
					if (modelName) {
						setModels([{
							value: initialValues.modelId,
							label: modelName,
						}]);
					}
					console.error("Error restoring model:", error);
				} finally {
					setLoadingModels(false);
				}
			}
		};
		restoreData();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues]);

	// Manejar cambio de año (interacción del usuario)
	const handleYearChange = (newYear) => {
		isRestoringRef.current = false;
		setYear(newYear);
		setSelectedBrand(null);
		setSelectedModel(null);
		setBrands([]);
		setModels([]);
		if (newYear) {
			loadBrandsByYear(newYear);
		}
	};

	// Manejar búsqueda en marcas
	const handleBrandSearch = (searchValue) => {
		if (year) {
			searchBrands(year, searchValue);
		}
	};

	// Manejar búsqueda en modelos
	const handleModelSearch = (searchValue) => {
		if (selectedBrand) {
			searchModels(selectedBrand, searchValue);
		}
	};

	// Manejar selección de marca (interacción del usuario)
	const handleBrandChange = (brandId) => {
		isRestoringRef.current = false;
		setSelectedBrand(brandId);
		setSelectedModel(null);
		setModels([]);
		if (brandId) {
			loadModelsByBrand(brandId, year);
		}
	};

	// Establecer valores iniciales en el formulario
	useEffect(() => {
		if (initialValues) {
			form.setFieldsValue(initialValues);
		}
	}, [initialValues, form]);

	const handleNextStep = (values) => {
		const selectedModelData = models.find(m => m.value === selectedModel);
		const selectedBrandData = brands.find(b => b.value === selectedBrand);
		
		const updatedValues = {
			// Información básica
			year: values.year,
			
			// Información de la marca
			brandId: selectedBrand,
			brandName: selectedBrandData?.label,
			
			// Información del modelo
			modelId: selectedModel, // Este es el CODIA
			codia: selectedModel, // CODIA de InfoAuto (código único del modelo)
			modelName: selectedModelData?.label, // Descripción del modelo
			position: selectedModelData?.position, // Position de InfoAuto
			
			// Información adicional para cotizaciones
			description: selectedModelData?.label, // Descripción completa del modelo
			photo_url: selectedModelData?.photo_url, // URL de la foto del modelo
		};

		updateReportForm(updatedValues, 0);
		nextSubStep(2);
	};

	return (
		<Form
			form={form}
			name="car-search-form"
			initialValues={initialValues}
			onFinish={handleNextStep}
		>
			<Inner>
				<Typo level={isMobile ? 3 : 2} style={{ whiteSpace: "nowrap" }}>
					¿Qué auto estás asegurando?
				</Typo>
				
				{/* Primer paso: Año del vehículo */}
				<Section isMobile={isMobile}>
					<Form.Item 
						name="year" 
						rules={[
							{ required: true, message: "Por favor ingrese el año" },
							{ 
								pattern: /^(19|20)\d{2}$/, 
								message: "Ingrese un año válido (ej: 2020)" 
							}
						]}
						style={{ marginBottom: 0 }}
					>
						<InputNumber
							placeholder="Año del vehículo"
							style={{ minWidth: "250px" }}
							min={1990}
							max={new Date().getFullYear() + 1}
							onChange={handleYearChange}
							parser={(value) => value.replace(/\D/g, '')} // Solo números
							formatter={(value) => value} // Sin formato especial
							controls={false} // Sin botones de incremento/decremento
							onKeyPress={(e) => {
								// Prevenir entrada de caracteres no numéricos
								if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'Enter') {
									e.preventDefault();
								}
							}}
						/>
					</Form.Item>
				</Section>

				{/* Segundo paso: Marca (solo si hay año) */}
				{year && (
					<Section isMobile={isMobile}>
						<Select
							showSearch
							placeholder={loadingBrands ? "Cargando marcas..." : "Buscar marca"}
							loading={loadingBrands}
							value={selectedBrand}
							onChange={handleBrandChange}
							onSearch={handleBrandSearch}
							filterOption={false} // Desactivar filtro local, usar búsqueda del servidor
							options={brands}
							style={{ width: 250 }}
							notFoundContent={loadingBrands ? "Cargando..." : "No se encontraron marcas"}
						/>
					</Section>
				)}

				{/* Tercer paso: Modelo (solo si hay marca seleccionada) */}
				{selectedBrand && (
					<Section isMobile={isMobile}>
						<Select
							showSearch
							placeholder={loadingModels ? "Cargando modelos..." : "Buscar modelo"}
							loading={loadingModels}
							value={selectedModel}
							onChange={setSelectedModel}
							onSearch={handleModelSearch}
							filterOption={false} // Desactivar filtro local, usar búsqueda del servidor
							options={models}
							style={{ width: isMobile ? "100%" : 400, minWidth: 300 }}
							notFoundContent={loadingModels ? "Cargando..." : "No se encontraron modelos"}
						/>
					</Section>
				)}

				{/* Botón de siguiente (solo si todos los campos están completos) */}
				{year && selectedBrand && selectedModel && (
					<Form.Item style={{ marginBottom: 0 }}>
						<AntdButton type="primary" htmlType="submit">
							<Typo level={5} type="white">
								Siguiente
							</Typo>
						</AntdButton>
					</Form.Item>
				)}
			</Inner>
		</Form>
	);
};

export default CarSearch;
